import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../../hooks/useTheme';
import { useCartStore } from '../../store/cart.store';
import { CartDrawer } from '../cart/CartDrawer';
import { SearchResults } from '../search/SearchResults';
import { ProductsService } from '../../services/products/products.service';
import type { ProductSearchResult } from '../../services/products/products.service';
import { Search, ShoppingCart, Truck, Phone, Package } from 'lucide-react';
import logoSinTexto from '../../assets/Logo_sin_texto.png';
import textoLogo from '../../assets/Texto logo.png';

export const Header = () => {
  const { theme } = useTheme();
  const { getTotalItems, openCart } = useCartStore();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ProductSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchTimeoutRef = useRef<number | null>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const totalItems = getTotalItems();

  const navItems = [
    { name: 'Inicio', path: '/' },
    { name: 'Productos', path: '/productos' },
    { name: 'Servicios', path: '/servicios' },
    { name: 'Contacto', path: '/contacto' },
  ];

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // Función de búsqueda en tiempo real
  const performSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    setShowResults(true);

    try {
      const response = await ProductsService.search(query, 8);
      if (response.success) {
        setSearchResults(response.data);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounce para la búsqueda
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value.trim()) {
      searchTimeoutRef.current = window.setTimeout(() => {
        performSearch(value);
      }, 300);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  };

  // Submit del formulario de búsqueda
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setShowResults(false);
      window.location.href = `/productos?search=${encodeURIComponent(searchQuery)}`;
    }
  };

  // Cerrar resultados
  const handleCloseResults = () => {
    setShowResults(false);
  };

  // Agregar al carrito desde búsqueda
  const handleAddToCartFromSearch = (product: ProductSearchResult) => {
    // Por ahora solo mostraremos un mensaje, ya que no tenemos addToCart en el store
    alert(`${product.nombre} agregado al carrito`);
    setShowResults(false);
  };

  // Efecto para cerrar resultados al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Limpiar timeout al desmontar
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Barra superior con info de contacto y domicilios */}
      <div className={`hidden md:block py-2 px-4 lg:px-8 text-sm ${
        theme === 'dark'
          ? 'bg-[#0d5c3b] text-green-100'
          : 'bg-gradient-to-r from-[#4CAF50] to-[#2196F3] text-white'
      }`}>
        <div className="w-full flex justify-between items-center px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-6">
            <span className="flex items-center">
              <Truck className="w-4 h-4 mr-2" />
              Domicilios gratis en pedidos mayores a $50.000
            </span>
            <a 
              href="https://wa.me/573193650538"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center hover:text-emerald-200 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.567-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.50 3.488"/>
              </svg>
              WhatsApp: (+57) 319 3650538
            </a>
          </div>
          <div className="flex items-center space-x-4">
            <span>Horario: Lun-Dom 8:00 AM - 9:00 PM</span>
          </div>
        </div>
      </div>

      {/* Header principal */}
      <nav className={`transition-all duration-500 ${
        theme === 'dark'
          ? 'bg-gray-900/95 border-gray-700/30'
          : 'bg-white/95 border-gray-200/30'
      } backdrop-blur-xl border-b shadow-lg`}>
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 group">
              {/* Logo sin texto - icono */}
              <div className="w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-105 bg-white shadow-md p-1">
                <img 
                  src={logoSinTexto} 
                  alt="ConfiFarma Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              {/* Texto del logo */}
              <div className="hidden sm:block">
                <img 
                  src={textoLogo} 
                  alt="ConfiFarma Amiga" 
                  className={`h-8 w-auto transition-all duration-300 ${
                    theme === 'dark' ? 'brightness-110' : ''
                  }`}
                />
              </div>
            </Link>

            {/* Barra de búsqueda - Desktop */}
            <div className="hidden lg:flex flex-1 max-w-2xl mx-8" ref={searchContainerRef}>
              <form onSubmit={handleSearch} className="w-full relative">
                <input
                  type="text"
                  placeholder="Buscar medicamentos, productos..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className={`w-full pl-4 pr-12 py-2.5 rounded-full border-2 transition-all duration-300 ${
                    theme === 'dark'
                      ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:border-[#4CAF50] focus:ring-2 focus:ring-[#4CAF50]/20'
                      : 'bg-gray-100 border-gray-200 text-gray-900 placeholder-gray-500 focus:border-[#2196F3] focus:ring-2 focus:ring-[#2196F3]/20'
                  }`}
                />
                <button 
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-gradient-to-r from-[#4CAF50] to-[#2196F3] hover:opacity-90 text-white rounded-full transition-all"
                >
                  <Search className="w-4 h-4" />
                </button>
                
                {/* Resultados de búsqueda */}
                <SearchResults
                  results={searchResults}
                  isLoading={isSearching}
                  query={searchQuery}
                  isVisible={showResults}
                  onClose={handleCloseResults}
                  onAddToCart={handleAddToCartFromSearch}
                />
              </form>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                    isActive(item.path)
                      ? 'bg-gradient-to-r from-[#4CAF50]/20 to-[#2196F3]/20 text-[#0d5c3b] dark:text-[#4CAF50]'
                      : theme === 'dark'
                        ? 'text-gray-300 hover:text-white hover:bg-gray-800'
                        : 'text-gray-600 hover:text-[#2196F3] hover:bg-gray-100'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Right side buttons */}
            <div className="flex items-center space-x-3">
              {/* Carrito */}
              <button 
                onClick={openCart}
                className={`relative p-2 rounded-lg transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:text-white hover:bg-gray-800'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <ShoppingCart className="w-6 h-6" />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-[#4CAF50] to-[#2196F3] text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                    {totalItems > 99 ? '99+' : totalItems}
                  </span>
                )}
              </button>

              {/* Mis Pedidos */}
              <Link
                to="/pedidos"
                className={`hidden sm:flex items-center space-x-1 px-3 py-2 rounded-lg font-medium transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:text-white hover:bg-gray-800'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Package className="w-5 h-5" />
                <span className="text-sm">Mis Pedidos</span>
              </Link>

              {/* Tema */}
              <div className={`p-1 rounded-lg ${
                theme === 'dark'
                  ? 'bg-gray-800/50 ring-1 ring-gray-700/50'
                  : 'bg-gray-100/50 ring-1 ring-gray-200/50'
              }`}>
              </div>

              {/* Login button */}
              <Link
                to="/login"
                className="hidden sm:flex items-center px-4 py-2 bg-gradient-to-r from-[#4CAF50] to-[#2196F3] hover:opacity-90 text-white rounded-lg font-medium transition-all shadow-md"
              >
                Ingresar
              </Link>
              
              {/* Mobile menu button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`lg:hidden p-2 rounded-lg transition-all duration-300 ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:text-white hover:bg-gray-800'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          <div className={`lg:hidden transition-all duration-300 overflow-hidden ${
            isMenuOpen ? 'max-h-screen pb-4' : 'max-h-0'
          }`}>
            {/* Search Mobile */}
            <div className="mb-4 relative">
              <form onSubmit={handleSearch} className="relative">
                <input
                  type="text"
                  placeholder="Buscar medicamentos..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className={`w-full pl-4 pr-12 py-3 rounded-xl border-2 transition-all duration-300 ${
                    theme === 'dark'
                      ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:border-[#4CAF50]'
                      : 'bg-gray-100 border-gray-200 text-gray-900 placeholder-gray-500 focus:border-[#2196F3]'
                  }`}
                />
                <button 
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-gradient-to-r from-[#4CAF50] to-[#2196F3] hover:opacity-90 text-white rounded-lg transition-all"
                >
                  <Search className="w-5 h-5" />
                </button>
              </form>
              
              {/* Resultados de búsqueda móvil */}
              {isMenuOpen && (
                <SearchResults
                  results={searchResults}
                  isLoading={isSearching}
                  query={searchQuery}
                  isVisible={showResults}
                  onClose={handleCloseResults}
                  onAddToCart={handleAddToCartFromSearch}
                />
              )}
            </div>

            {/* Nav items */}
            <div className="space-y-2">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                    isActive(item.path)
                      ? 'bg-gradient-to-r from-[#4CAF50]/20 to-[#2196F3]/20 text-[#0d5c3b] dark:text-[#4CAF50]'
                      : theme === 'dark'
                        ? 'text-gray-300 hover:text-white hover:bg-gray-800'
                        : 'text-gray-600 hover:text-[#2196F3] hover:bg-gray-100'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              
              <button
                onClick={() => {
                  setIsMenuOpen(false);
                  openCart();
                }}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:text-white hover:bg-gray-800'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <span>Carrito</span>
                {totalItems > 0 && (
                  <span className="px-2 py-1 bg-red-500 text-white text-sm rounded-full">
                    {totalItems}
                  </span>
                )}
              </button>
              
              <Link
                to="/login"
                onClick={() => setIsMenuOpen(false)}
                className="block px-4 py-3 bg-gradient-to-r from-[#4CAF50] to-[#2196F3] text-white rounded-lg font-medium text-center hover:opacity-90 transition-all"
              >
                Ingresar / Registrarse
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Cart Drawer */}
      <CartDrawer />
    </header>
  );
};

export default Header;
